<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      existingAttachments: [...this.edattachments],
      attachments: [],
      attachmentPreviews: [],
      attachmentsToDelete: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.existingAttachments = [];
      this.attachments = [];
      this.attachmentPreviews = [];
      this.attachmentsToDelete = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    handleFileUpload(event) {
      const files = event.target.files;
      Array.from(files).forEach(file => {
        this.attachments.push(file);
        const isVideo = file.type.startsWith('video/');
        const url = URL.createObjectURL(file);
        this.attachmentPreviews.push({ url, isVideo });
      });
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
      this.attachmentPreviews.splice(index, 1);
    },
    removeExistingAttachment(index) {
      const attachment = this.existingAttachments[index];
      this.attachmentsToDelete.push(attachment.id);
      this.existingAttachments.splice(index, 1);
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;

      const formData = new FormData();
      formData.append('id', this.id);
      formData.append('canned_response[short_code]', this.shortCode);
      formData.append('canned_response[content]', this.content);

      this.attachments.forEach(file => {
        formData.append('attachments[]', file);
      });

      this.attachmentsToDelete.forEach(id => {
        formData.append('canned_response[attachments_attributes][][id]', id);
        formData.append(
          'canned_response[attachments_attributes][][_destroy]',
          '1'
        );
      });

      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', formData)
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full mt-4">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.LABEL') }}
          </label>
          <div class="file-uploads">
            <input
              ref="fileInput"
              type="file"
              multiple
              accept="image/*,video/*"
              @change="handleFileUpload"
            />
            <div
              v-if="attachmentPreviews.length || existingAttachments.length"
              class="mt-2 flex gap-2 flex-wrap"
            >
              <!-- Existing Attachments -->
              <div
                v-for="(attachment, index) in existingAttachments"
                :key="`existing-${attachment.id}`"
                class="relative"
              >
                <video
                  v-if="attachment.file_type === 'video'"
                  :src="attachment.file_url"
                  class="w-20 h-20 object-cover rounded border border-slate-200 dark:border-slate-700"
                />
                <img
                  v-else
                  :src="attachment.thumb_url"
                  class="w-20 h-20 object-cover rounded border border-slate-200 dark:border-slate-700"
                />
                <button
                  class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  type="button"
                  @click="removeExistingAttachment(index)"
                >
                  <fluent-icon icon="dismiss" size="12" />
                </button>
              </div>

              <!-- New Attachments -->
              <div
                v-for="(preview, index) in attachmentPreviews"
                :key="`new-${index}`"
                class="relative"
              >
                <video
                  v-if="preview.isVideo"
                  :src="preview.url"
                  class="w-20 h-20 object-cover rounded border border-slate-200 dark:border-slate-700"
                />
                <img
                  v-else
                  :src="preview.url"
                  class="w-20 h-20 object-cover rounded border border-slate-200 dark:border-slate-700"
                />
                <button
                  class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  type="button"
                  @click="removeAttachment(index)"
                >
                  <fluent-icon icon="dismiss" size="12" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
